/**
 * 型錄匯出工具
 * PDF: 使用隱藏 iframe + 瀏覽器列印對話框
 * Excel: 使用 ExcelJS，版面與 PDF 完全一致（3欄×4列卡片網格 + 公司表頭 + 產品圖片）
 */

const CARDS_PER_ROW = 3;
const ROWS_PER_PAGE = 4;
const CARDS_PER_PAGE = CARDS_PER_ROW * ROWS_PER_PAGE; // 12

// ── ARGB 色票（與 PDF 相同主題色）
const C = {
  headerBg:   'FFFFF8E1', // 表頭背景（米黃）
  badge:      'FFFFD54F', // 金色標籤 (#FFD54F)
  dark:       'FF5D4037', // 深棕文字
  mid:        'FF8D6E63', // 中棕文字
  cardBorder: 'FFD4B483', // 卡片外框
  innerBord:  'FFE6C9A3', // 卡片內分隔線
  white:      'FFFFFFFF',
  // 需求 R93（比照 需求 R91 報價單）：產品圖片底色由 imageBg('FFF0E0B2') 改為白色。
  // 原 imageBg 常數在本檔的唯一用途即產品圖格底色，改用 C.white 後無任何引用故移除。
  emptyCard:  'FFFAFAFA',
};

// ── 欄位結構：每張卡片佔 3 個 Excel 欄，卡片之間無分隔欄（對應 PDF `gap: 0`）──
// 需求 R7（2026-05-18 修正）：原本卡片間多插一個窄分隔欄會讓客戶並排比對時看到
// Excel 卡片明顯外撐 ≈ 2.4 字元，造成版面與 PDF 不一致。
// A B C | D E F | G H I
const CARD_COLS = [
  [1, 2, 3],
  [4, 5, 6],
  [7, 8, 9],
];
const TOTAL_COLS = 9;
// 每個子欄寬度（字元單位）→ 卡片寬必須等於 PDF .product-card 的 6.5cm。
// 需求 R7（2026-05-22 PM 回饋「欄位的設定也有與PDF 不符」）：
// 舊值 11.7 經 Excel 公式換算 px = width × 7 + 5 ≈ 86.9 px ≈ 2.30 cm，
// 3 個子欄組成的卡片實際寬度 ≈ 6.89 cm，比 PDF 6.5 cm 大 0.4 cm。
// 並排比對時可直接看出 Excel 卡片明顯外撐，且整列 9 欄合計
// 3 × 6.89 = 20.67 cm 接近 A4 (21 cm) 邊界，造成 fitToPage 觸發縮放，
// 使印出來的卡片變形變模糊。
// 新值 11.0：px = 11.0 × 7 + 5 = 82 px ≈ 2.17 cm/sub-col，
// 卡片寬度 = 3 × 2.17 = 6.51 cm 完全對齊 PDF。整列 9 欄合計 19.5 cm，
// 與 PDF 的 .products-grid (grid-template-columns: repeat(3, 6.5cm)) 一致。
// eslint-disable-next-line no-unused-vars
const CARD_W = 11.0;

// 需求 R78（PM 2026-07-21）：三個子欄不再等寬 —「貨編」縮小、「規格」加寬，與 PDF 端
// 同方向（貨編最多 7 字、把空間讓給規格避免跳行）。三欄合計仍維持 33（= 3 × CARD_W），
// 卡片外寬 6.5cm、整列 9 欄 = 19.5cm、水平置中基準皆不變，不影響 需求 R7/109 版面。
// 下限：「貨編」欄為卡片 1 的 A 欄，公司 Logo（60px = 寬度約 7.86）浮貼於此，故貨編欄
// 寬不可小於 8，否則 Logo 會溢出到公司名稱區。取 8 / 14 / 11（合計 33）。
// 二欄列（備註合併 A+B = 8+14 = 22、保存期限 C = 11）與原本 22 / 11 完全一致。
const COL_W_CODE = 8.0;
const COL_W_SPEC = 14.0;
const COL_W_BOX  = 11.0;

// ── 卡片每列高度（pt，1cm ≈ 28.35pt）對應 PDF 卡片區塊
// PDF .product-card = 6.5cm × 6.5cm ≈ 184pt；.product-image = 4.5cm；.product-info = 2cm。
const ROW_H_IMG    = 128;  // 圖片區 4.5cm
const ROW_H_THREE  = 19;   // 三欄資訊（貨編/規格/箱入數）
const ROW_H_NAME   = 20;   // 品名 + 英文品名
const ROW_H_TWO    = 17;   // 備註 + 保存期限  (128+19+20+17 = 184pt ≈ 6.5cm)

/**
 * 抓取網路圖片並回傳已載入的 HTMLImageElement 與原圖尺寸
 * — 後續會以 canvas 預先渲染成「指定長寬比 + bg 填色 + contain mode」的 PNG
 */
async function fetchImageElement(url) {
  if (!url) return null;
  try {
    const fullUrl = url.startsWith('http')
      ? url
      : `${window.location.origin}${url.startsWith('/') ? '' : '/'}${url}`;
    const response = await fetch(fullUrl);
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise((resolve) => {
      const objectUrl = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => resolve({ img, objectUrl, naturalW: img.naturalWidth || 0, naturalH: img.naturalHeight || 0 });
      img.onerror = () => { URL.revokeObjectURL(objectUrl); resolve(null); };
      img.src = objectUrl;
    });
  } catch (err) {
    console.warn('fetchImageElement failed:', url, err);
    return null;
  }
}

/**
 * 把圖片以 canvas 預先渲染成「指定 boxW×boxH（PNG 像素 = 目標版面長寬比）+ 周圍填 bg + contain mode」
 *
 * 需求 R7 圖片變形修正：
 *   原本依賴 ExcelJS 的 `tl + ext + editAs: 'oneCell'` 期望 Excel 用 ext 像素值
 *   保持圖片原寸，但部分 viewer（Mac Numbers / Google Sheets / LibreOffice）並不
 *   honor `oneCell`，仍會把圖片拉伸到卡片矩形（1.44:1）導致明顯變形。
 *
 *   本函式在 client 端先把圖片畫到「與目標卡片區同長寬比」的 canvas 上（contain
 *   mode + 背景填卡片底色），輸出 PNG buffer。之後不論 Excel 是否拉伸，PNG 內容
 *   已自帶正確比例 + bg padding，視覺等同 PDF 的 object-fit: contain，徹底消除
 *   變形問題。
 *
 * @param {HTMLImageElement} imgEl  已載入的原圖
 * @param {number} natW             原圖寬（px）
 * @param {number} natH             原圖高（px）
 * @param {number} boxW             輸出 canvas 寬（建議與目標版面 ratio 一致）
 * @param {number} boxH             輸出 canvas 高
 * @param {string|null} bgColor     背景填色（CSS 字串）；null = 透明
 * @param {number} padding          內側留白（px），避免內容貼到 Excel 卡片外框線
 * @returns {Promise<ArrayBuffer>}  PNG 的 ArrayBuffer
 */
async function renderContainedToBuffer(imgEl, natW, natH, boxW, boxH, bgColor, padding = 0) {
  const canvas = document.createElement('canvas');
  canvas.width = boxW;
  canvas.height = boxH;
  const ctx = canvas.getContext('2d');
  if (bgColor) {
    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, boxW, boxH);
  } else {
    ctx.clearRect(0, 0, boxW, boxH);
  }
  const innerW = Math.max(1, boxW - padding * 2);
  const innerH = Math.max(1, boxH - padding * 2);
  const w = natW > 0 ? natW : innerW;
  const h = natH > 0 ? natH : innerH;
  const scale = Math.min(innerW / w, innerH / h);
  const drawW = Math.max(1, Math.round(w * scale));
  const drawH = Math.max(1, Math.round(h * scale));
  const drawX = Math.round((boxW - drawW) / 2);
  const drawY = Math.round((boxH - drawH) / 2);
  ctx.drawImage(imgEl, drawX, drawY, drawW, drawH);
  const renderedBlob = await new Promise(res => canvas.toBlob(res, 'image/png'));
  return await renderedBlob.arrayBuffer();
}

/**
 * Excel 匯出 — 版面與 PDF 一致（含產品圖片、公司 Logo、分頁）
 */
export async function exportCatalogExcel({ products, companyInfo = {}, title, contact = {}, filename }) {
  const { Workbook } = await import('exceljs');
  const workbook = new Workbook();
  // 預先計算總頁數，pageSetup 的 fitToHeight 必須給予明確頁數，否則部分 Excel viewer
  // 在 fitToHeight=0 時會把整份文件當「一頁」處理或忽略手動分頁符。
  const totalPages = Math.max(1, Math.ceil(products.length / CARDS_PER_PAGE));
  const sheet = workbook.addWorksheet('產品型錄', {
    pageSetup: {
      paperSize: 9, // A4
      orientation: 'portrait',
      fitToPage: true,
      fitToWidth: 1,
      // 需求 R7（2026-05-22 PM 回饋「文件設定上有未滿版」）：
      // 舊值 0 意指「高度不限」，但 Excel / Mac Numbers / Google Sheets 在
      // fitToHeight=0 時行為不一致：部分 viewer 不會 honor 手動分頁符、
      // 部分 viewer 會把整份壓進一頁。改為傳入實際頁數 totalPages，
      // 明確告訴 viewer「總共 N 頁、寬度 1 頁」，每頁版面才會完整滿版輸出。
      fitToHeight: totalPages,
      // 需求 R7（PM 2026-05-21 回饋）：原本以 printTitlesRow:'1:5'
      // 試圖讓表頭於列印第 2、3 頁自動重複，實測 Excel / Numbers / Google Sheets
      // 列印預覽行為不一致（部分 viewer 完全不會 honor），改採「在每一頁實體插入
      // 一份表頭區塊」的做法，列印預覽必定看得到公司資訊 + 金色分隔條，與 PDF 一致。
      //
      // 需求 R7（2026-05-22 PM 回饋「文件設定上有未滿版」）：
      // 舊值 margin = 0.2 inch（約 0.51 cm）會在 A4 邊緣留下明顯白邊，
      // 而 PDF 端設定為 `@page { size: A4; margin: 0 }` 完全滿版。
      // 兩者比對下 Excel 看起來像「縮在中間沒滿版」。
      // 改採 margin = 0 對齊 PDF；並用 horizontalCentered:true 對應 PDF
      // .catalog-body { justify-content: center }，讓 9 欄 = 19.5 cm 的
      // 卡片網格在 A4 (21 cm) 寬度中水平置中（左右各 0.75 cm 留白，
      // 與 PDF 視覺完全一致）。
      horizontalCentered: true,
      verticalCentered: false,
      margins: { left: 0, right: 0, top: 0, bottom: 0, header: 0, footer: 0 },
      // 需求 R38（PM 2026-05-25 回饋「列印預覽右側多出空欄」）：
      // 未設 printArea 時，Excel / Numbers / Google Sheets 會以「最後一筆寫入」
      // 自動推算列印範圍，部分 viewer 會把 J 欄或更後面的欄一併納入，使 9 欄
      // (= 19.5cm) 的卡片網格在 A4 (21cm) 上無法水平置中（horizontalCentered=true
      // 雖然有設，但是以實際列印範圍寬度為基準，多一欄就會被推往左側）。
      // 明確指定 'A1:I{totalPages*22}'（每頁 22 列 = 6 列表頭 + 16 列卡片區 ×4 卡）
      // 告訴 viewer 列印範圍嚴格只到 I 欄，左右白邊對稱。
      printArea: `A1:I${totalPages * 22}`,
    },
    // 需求 R38（PM 2026-05-25 二次回饋「並沒有完成範圍、
    // 列印置中、12 品項/頁、每頁皆有表頭」）：
    // first commit (78fb923) 已經設 pageSetup.printArea，但 PM 在 Mac
    // Numbers / Google Sheets 列印預覽仍看到 J 欄空白佔位（截圖證據）。
    // 實測：部分 viewer 即便 sheet 真實使用範圍只到 I 欄，仍會把預覽
    // 視窗的可視欄一併納入列印版面（典型情境：把 sheet 開到 J ~ Z 是
    // 「visible default」），導致 horizontalCentered=true 的基準寬度
    // 被拉大到 J 欄以後，內容看起來偏左、右側多出空欄。
    // 策略：用 worksheet view 把 showGridLines / showRowColHeaders
    // 關閉，視覺上不再有「右側空欄」的存在感（搭配下方 J 欄之後 hidden
    // + width=0 一起把列印範圍鎖死在 A:I）。
    views: [{ showGridLines: false, showRowColHeaders: false }],
  });

  // 欄寬設定：9 欄 = 3 張卡片 × 3 子欄，卡片之間相鄰（對應 PDF `gap: 0`）
  // 需求 R38（PM 2026-05-25 二次回饋）：
  // 除了 pageSetup.printArea 限定 A:I 之外，再顯式宣告 J ~ R 共 9 欄
  // `hidden: true, width: 0`。OOXML 規範中 hidden 欄不會出現在列印
  // 範圍，亦不會被 horizontalCentered 的版面寬度計算納入。雙重保險
  // 後，Mac Numbers / Google Sheets / Excel 三方列印預覽都只會看到
  // A:I 共 9 欄 = 19.5 cm 的卡片網格水平置中於 A4 (21 cm)。
  sheet.columns = [
    // 需求 R78：每張卡片三子欄寬 = 貨編 8 / 規格 14 / 箱入數 11（合計 33 = 3×CARD_W 不變）
    { width: COL_W_CODE }, { width: COL_W_SPEC }, { width: COL_W_BOX }, // 卡片 1
    { width: COL_W_CODE }, { width: COL_W_SPEC }, { width: COL_W_BOX }, // 卡片 2
    { width: COL_W_CODE }, { width: COL_W_SPEC }, { width: COL_W_BOX }, // 卡片 3
    // J ~ R：顯式 hidden + width=0，明確告訴所有 viewer 此 sheet 在 I 欄之後
    // 沒有任何要列印 / 顯示的內容，避免被當成「可視預設欄」拖入列印範圍。
    { width: 0, hidden: true }, { width: 0, hidden: true }, { width: 0, hidden: true },
    { width: 0, hidden: true }, { width: 0, hidden: true }, { width: 0, hidden: true },
    { width: 0, hidden: true }, { width: 0, hidden: true }, { width: 0, hidden: true },
  ];

  // ── 輔助函式
  const solid = (argb) => ({ type: 'pattern', pattern: 'solid', fgColor: { argb } });
  const bs    = (argb, style = 'thin') => ({ style, color: { argb } });

  const fillRect = (r1, c1, r2, c2, argb) => {
    for (let r = r1; r <= r2; r++)
      for (let c = c1; c <= c2; c++)
        sheet.getCell(r, c).fill = solid(argb);
  };

  const cl = (r, c) => sheet.getCell(r, c);

  const mergeSet = (r1, c1, r2, c2, value, { fill, font, align, border } = {}) => {
    if (fill) fillRect(r1, c1, r2, c2, fill);
    if (r1 !== r2 || c1 !== c2) sheet.mergeCells(r1, c1, r2, c2);
    const cell = cl(r1, c1);
    cell.value = value;
    if (fill)   cell.fill      = solid(fill);
    if (font)   cell.font      = { name: 'Microsoft JhengHei', ...font };
    if (align)  cell.alignment = align;
    if (border) cell.border    = border;
    return cell;
  };

  // ── 預先抓取所有圖片並以 canvas 預先渲染（contain mode + bg padding）──
  // 產品圖卡片區的目標 canvas 長寬比 = 卡片實際顯示比例（3 × CARD_W 字元 ÷ ROW_H_IMG pt）
  // 採用 720×494（解析度 ×3 以兼顧列印品質）；周圍填白色（需求 R93 前為 #F0E0B2），避免變形
  const PRODUCT_BOX_W = 720;
  const PRODUCT_BOX_H = 494; // 720 / (CARD_W*3*7 / (ROW_H_IMG*96/72)) ≈ 720/1.46 ≈ 494
  // 需求 R93：contain mode 留白處會被烤進 PNG，故必須與儲存格底色（C.white）同步改白，
  // 否則圖片四周仍殘留一圈 #F0E0B2。
  const PRODUCT_BG    = '#FFFFFF';
  const PRODUCT_PAD   = 12; // ≈ 4px 在實際卡片上的等效 padding
  const LOGO_BOX      = 240; // 60×60 顯示，預先用 240×240（×4 解析度）的透明 canvas 包住
  // 需求 R38：addImage(tl + ext) 用的目標像素長寬，對應 3 個 CARD_W 子欄 × ROW_H_IMG。
  // 3 cols × (CARD_W × 7 + 5) px ≈ 3 × 82 = 246 px；ROW_H_IMG (128pt) × 96/72 = 170.67 px。
  // PNG 本身已用 PRODUCT_BOX_W × PRODUCT_BOX_H = 720 × 494（同長寬比 1.458）+
  // bg padding 預渲染好，cell 端用 246 × 171 顯示不會變形。
  const PRODUCT_IMG_BOX_PX_W = 246;
  const PRODUCT_IMG_BOX_PX_H = 171;

  const productImageMap = {};
  const imageFetchTasks = products.map(async (p) => {
    if (!p.productImage) return;
    const fetched = await fetchImageElement(p.productImage);
    if (!fetched) return;
    try {
      const buffer = await renderContainedToBuffer(
        fetched.img, fetched.naturalW, fetched.naturalH,
        PRODUCT_BOX_W, PRODUCT_BOX_H,
        PRODUCT_BG, PRODUCT_PAD,
      );
      productImageMap[p._id] = { buffer, extension: 'png' };
    } finally {
      URL.revokeObjectURL(fetched.objectUrl);
    }
  });

  const logoPromise = (async () => {
    if (!companyInfo.logoUrl) return null;
    const fetched = await fetchImageElement(companyInfo.logoUrl);
    if (!fetched) return null;
    try {
      // 透明背景的方形 canvas → logo 在其中以 contain mode 置中，
      // 無論原圖比例為何，置入 60×60 後皆保持原比例不變形
      const buffer = await renderContainedToBuffer(
        fetched.img, fetched.naturalW, fetched.naturalH,
        LOGO_BOX, LOGO_BOX,
        null, 0,
      );
      return { buffer, extension: 'png' };
    } finally {
      URL.revokeObjectURL(fetched.objectUrl);
    }
  })();

  const [logoData] = await Promise.all([logoPromise, ...imageFetchTasks]);

  // ── 表頭區塊（共 6 列）對應 PDF .catalog-header
  // 字級換算：PDF px × 0.75 = Excel pt（與 quoteExcelExport.js 一致）。
  // 16px → 12pt   (.company-details h1 公司中文)
  // 11px → 8.25pt (.catalog-title-badge 型錄標題徽章)
  // 10px → 7.5pt  (.company-details p — nameEn / address / addressEn / Tel/Fax)
  // 9px → 7pt    (.contact-info 聯絡人區塊整段)
  // 字型統一 Microsoft JhengHei（與報價單 Excel 對齊）
  //
  // 需求 R7（PM 2026-05-22）：版面再次對齊 PDF，表頭由 5 列改為 6 列：
  // - 中/英文地址各自獨立成一行（原本以 ' / ' 串接成單行，與 PDF 不一致）
  // - 「聯絡人 Contact」獨立成 label 列（PDF 為 <span class="contact-title">），
  // 原本 inline 串接 "聯絡人 Contact: 名字" 不符 PDF 排版
  // - 右側資訊：badge / Contact label / 姓名 / 電話 / Line or Email（5 列）
  // - 左側資訊：公司中文 / 公司英文 / 中文地址 / 英文地址 / Tel/Fax（5 列）
  // 此區塊封裝為函式，於「每一頁」起始位置實體插入一份，列印預覽第 2、3 頁亦帶
  // 公司資訊 + 金色分隔條，使 PDF / Excel 兩方輸出版面一致。
  const telFax = [
    companyInfo.tel ? `Tel: ${companyInfo.tel}` : '',
    companyInfo.fax ? `Fax: ${companyInfo.fax}` : '',
  ].filter(Boolean).join('   ');
  // 需求 R81：聯絡人資料改顯示 E-mail（無「標題」直接呈現值）；
  // 通訊方式（原 lineId）不顯示於型錄匯出。
  const contactEmail = contact.email || '';

  const buildHeaderBlock = (startRow) => {
    const hr1 = startRow;     // 公司中文 / title badge
    const hr2 = startRow + 1; // 公司英文 / 聯絡人 Contact label
    const hr3 = startRow + 2; // 中文地址 / 聯絡人姓名
    const hr4 = startRow + 3; // 英文地址 / 聯絡人電話
    const hr5 = startRow + 4; // Tel/Fax  / Line or Email
    const hr6 = startRow + 5; // 金色分隔條

    fillRect(hr1, 1, hr5, TOTAL_COLS, C.headerBg);

    sheet.getRow(hr1).height = 22;
    sheet.getRow(hr2).height = 13;
    sheet.getRow(hr3).height = 13;
    sheet.getRow(hr4).height = 13;
    sheet.getRow(hr5).height = 13;

    // 公司 Logo（每頁皆掛一份）— 預渲染方形 canvas，tl+ext 顯示為 60×60
    if (logoData) {
      const logoId = workbook.addImage({ buffer: logoData.buffer, extension: logoData.extension });
      sheet.addImage(logoId, {
        tl: { col: 0, row: hr1 - 1 },
        ext: { width: 60, height: 60 },
        editAs: 'oneCell',
      });
    }

    // 列1：公司名稱 + 型錄標題徽章
    mergeSet(hr1, 2, hr1, 5, companyInfo.name || '',
      { fill: C.headerBg, font: { size: 12, bold: true, color: { argb: C.dark } }, align: { vertical: 'middle', horizontal: 'left' } }
    );
    mergeSet(hr1, 6, hr1, TOTAL_COLS, title || '',
      { fill: C.badge, font: { size: 8.25, bold: true, color: { argb: C.dark } }, align: { vertical: 'middle', horizontal: 'center' } }
    );

    // 列2：英文公司名 + 「聯絡人 Contact」label（PDF .contact-title 9px mid）
    mergeSet(hr2, 2, hr2, 5, companyInfo.nameEn || '',
      { fill: C.headerBg, font: { size: 7.5, color: { argb: C.mid } }, align: { vertical: 'middle', horizontal: 'left' } }
    );
    if (contact.name) {
      // 需求 R81 D2（PM 2026-07-22 拍板）：「聯絡人 Contact」區塊標題保留；僅移除欄位級標題
      mergeSet(hr2, 6, hr2, TOTAL_COLS, '聯絡人 Contact',
        { fill: C.headerBg, font: { size: 7, color: { argb: C.mid } }, align: { vertical: 'middle', horizontal: 'right' } }
      );
    }

    // 列3：中文地址 + 聯絡人姓名（PDF .contact-detail 9px dark）
    mergeSet(hr3, 2, hr3, 5, companyInfo.address || '',
      { fill: C.headerBg, font: { size: 7.5, color: { argb: C.mid } }, align: { vertical: 'middle', horizontal: 'left' } }
    );
    if (contact.name) {
      mergeSet(hr3, 6, hr3, TOTAL_COLS, contact.name,
        { fill: C.headerBg, font: { size: 7, color: { argb: C.dark } }, align: { vertical: 'middle', horizontal: 'right' } }
      );
    }

    // 列4：英文地址 + 聯絡人 E-mail（需求 R81：改顯示 E-mail、無標題）
    mergeSet(hr4, 2, hr4, 5, companyInfo.addressEn || '',
      { fill: C.headerBg, font: { size: 7.5, color: { argb: C.mid } }, align: { vertical: 'middle', horizontal: 'left' } }
    );
    if (contactEmail) {
      mergeSet(hr4, 6, hr4, TOTAL_COLS, contactEmail,
        { fill: C.headerBg, font: { size: 7, color: { argb: C.dark } }, align: { vertical: 'middle', horizontal: 'right' } }
      );
    }

    // 列5：Tel/Fax（需求 R81：右側通訊方式/原 Line 不顯示於匯出）
    mergeSet(hr5, 2, hr5, 5, telFax,
      { fill: C.headerBg, font: { size: 7.5, color: { argb: C.mid } }, align: { vertical: 'middle', horizontal: 'left' } }
    );

    // 列6：金色分隔條（對應 PDF 表頭底部金線 2px solid #FFD54F）
    sheet.getRow(hr6).height = 4;
    fillRect(hr6, 1, hr6, TOTAL_COLS, C.badge);
  };

  // ── 產品卡片網格（每頁先插入 6 列表頭，再接 4 列卡片）
  // 每張卡片佔 4 個 Excel 列：
  // r0: 產品圖片（4.5cm 高）
  // r1: 三欄資訊（貨編/規格/箱入數）
  // r2: 品名 + 英文品名
  // r3: 兩欄資訊（備註/保存期限）
  // 每頁含 4 個卡片列（3 卡 × 4 列 = 12 卡），頁尾插入分頁符
  const oBord = bs(C.cardBorder);
  const iBord = bs(C.innerBord, 'hair');

  const pages = chunkArray(products, CARDS_PER_PAGE);
  // 需求 R7（2026-05-22）：每頁實體插入 6 列表頭區塊（含金色分隔條）。
  // 每頁版面 = 6 列表頭 + 16 列卡片區（4 列 × 4 子列）
  let curRow = 1;

  for (let pageIdx = 0; pageIdx < pages.length; pageIdx++) {
    buildHeaderBlock(curRow);
    curRow += 6;

    const pageProducts = pages[pageIdx];
    const cardRows = chunkArray(pageProducts, CARDS_PER_ROW);
    // 補滿至 4 列以維持版面
    while (cardRows.length < ROWS_PER_PAGE) cardRows.push([]);

    for (const cardRowGroup of cardRows) {
      const rImg = curRow;
      const r1 = curRow + 1; // three-cols
      const r2 = curRow + 2; // name
      const r3 = curRow + 3; // two-cols

      sheet.getRow(rImg).height = ROW_H_IMG;
      sheet.getRow(r1).height   = ROW_H_THREE;
      sheet.getRow(r2).height   = ROW_H_NAME;
      sheet.getRow(r3).height   = ROW_H_TWO;

      for (let ci = 0; ci < CARDS_PER_ROW; ci++) {
        const p = cardRowGroup[ci] || null;
        const [ca, cb, cc] = CARD_COLS[ci];

        if (!p) {
          // 空白佔位卡（對應 PDF .product-card-empty）
          fillRect(rImg, ca, r3, cc, C.emptyCard);
          for (let r = rImg; r <= r3; r++) {
            for (let c = ca; c <= cc; c++) {
              cl(r, c).border = {
                top:    r === rImg ? oBord : iBord,
                bottom: r === r3   ? oBord : iBord,
                left:   c === ca   ? oBord : iBord,
                right:  c === cc   ? oBord : iBord,
              };
            }
          }
          sheet.mergeCells(rImg, ca, rImg, cc);
          sheet.mergeCells(r2, ca, r2, cc);
          sheet.mergeCells(r3, ca, r3, cb);
          continue;
        }

        const code      = p.productCode || '—';
        const spec      = p.spec || '—';
        const pcsPerBox = p.pcsPerBox != null ? `${p.pcsPerBox}入/箱` : '—';
        const nameZh    = p.productName || '—';
        const nameEn    = p.englishName || '';
        const shelfLife = p.shelfLife || '—';
        // 需求 R95（PM 2026-07-31 拍板 Q1=A）：卡片「備註」欄的資料來源
        // 由「業務備註 salesNotes」改為「葷素備註 vegetarianType」（Product.js:56，純文字）。
        // Q1=A：只讀葷素備註，無值即空白，**不退回** salesNotes，與型錄列表「葷素備註」欄同口徑。
        const note      = p.vegetarianType || '';

        // r0：產品圖片（合併 ca-cc）
        // 需求 R93：底色 #F0E0B2 → 白色；無圖片時「產品圖」字色維持 C.mid(#8D6E63)。
        fillRect(rImg, ca, rImg, cc, C.white);
        sheet.mergeCells(rImg, ca, rImg, cc);
        const imgCell = cl(rImg, ca);
        imgCell.alignment = { vertical: 'middle', horizontal: 'center' };
        imgCell.border = { top: oBord, left: oBord, right: oBord, bottom: iBord };

        const productImg = productImageMap[p._id];
        if (productImg) {
          const imgId = workbook.addImage({ buffer: productImg.buffer, extension: productImg.extension });
          // 已預渲染為卡片長寬比 + bg 填色 + contain mode 的 PNG。
          //
          // 需求 R38（PM 2026-05-25 二次回饋 follow-up）：
          // 先前用 `tl + br: { col: cc, row: rImg }`，第 3 張卡片 cc=9 會輸出
          // `<xdr:to><xdr:col>9</xdr:col><xdr:colOff>0</xdr:colOff></xdr:to>`，
          // 雖然 colOff=0 視覺等同 I 欄右緣，但 Mac Numbers / Google Sheets
          // 會把 anchor 引用到的 col 9（J）算進 used range，導致即使
          // pageSetup.printArea 鎖 A:I、horizontalCentered=true 仍把卡片網格
          // 推到左半邊、右側多出空欄（PM 螢幕截圖佐證）。
          // 改用 `tl + ext`（oneCellAnchor 風格，純左上角 + 固定像素長寬），
          // anchor 完全不引用 col 9。圖片本身已是固定卡片長寬比 PNG，cell
          // 尺寸也是固定（CARD_W × 3 ≈ 246 px，ROW_H_IMG = 128pt ≈ 171 px），
          // 不會出現變形。三層保險：(1) printArea 鎖 A:I、(2) J~R 隱藏欄、
          // (3) anchor 不引用 J。
          sheet.addImage(imgId, {
            tl: { col: ca - 1, row: rImg - 1 },
            ext: { width: PRODUCT_IMG_BOX_PX_W, height: PRODUCT_IMG_BOX_PX_H },
            editAs: 'oneCell',
          });
        } else {
          imgCell.value = '產品圖';
          imgCell.font = { name: 'Microsoft JhengHei', size: 8, color: { argb: C.mid } };
        }

        // r1：三欄（貨編 / 規格 / 箱入數）— PDF .info-row.three-cols .info-cell 預設 LEFT 對齊，
        // label 7px (display:block) + value 9px font-weight:500
        // 字級對照（× 0.75）：7px → 6pt（label）、9px → 7pt（value）
        // 對齊：horizontal:left（PDF 預設）；垂直 top；indent 1 模擬 padding
        const threeColData = [
          { col: ca, label: '貨編',  value: code,
            borders: { top: iBord, left: oBord, right: iBord, bottom: iBord } },
          { col: cb, label: '規格',  value: spec,
            borders: { top: iBord, left: iBord, right: iBord, bottom: iBord } },
          { col: cc, label: '箱入數', value: pcsPerBox,
            borders: { top: iBord, left: iBord, right: oBord, bottom: iBord } },
        ];
        for (const { col, label, value, borders } of threeColData) {
          const cell = cl(r1, col);
          cell.value = { richText: [
            { text: label + '\n', font: { name: 'Microsoft JhengHei', size: 6, color: { argb: C.mid } } },
            { text: String(value), font: { name: 'Microsoft JhengHei', size: 7, bold: true, color: { argb: C.dark } } },
          ]};
          cell.fill      = solid(C.white);
          cell.alignment = { vertical: 'top', horizontal: 'left', wrapText: true, indent: 1 };
          cell.border    = borders;
        }

        // r2：全欄合併（品名 + 英文品名）
        // PDF .product-name-zh 10px font-weight:600 → 7.5pt bold；.product-name-en 7px italic → 6pt italic
        fillRect(r2, ca, r2, cc, C.white);
        sheet.mergeCells(r2, ca, r2, cc);
        const nameCell = cl(r2, ca);
        nameCell.value = { richText: [
          { text: nameZh + (nameEn ? '\n' : ''), font: { name: 'Microsoft JhengHei', size: 7.5, bold: true, color: { argb: C.dark } } },
          ...(nameEn ? [{ text: nameEn, font: { name: 'Microsoft JhengHei', size: 6, italic: true, color: { argb: C.mid } } }] : []),
        ]};
        nameCell.fill      = solid(C.white);
        nameCell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true, indent: 1 };
        nameCell.border    = { top: iBord, left: oBord, bottom: iBord, right: oBord };

        // r3：兩欄（備註合併 ca+cb，保存期限 cc）
        // PDF .info-row.two-cols .info-cell 為 flex-row align-center — label 與 value 同一行（以 `: ` 串接），
        // 而非 three-cols 的「label 在上、value 在下」。對應 Excel 不放 `\n`，使其顯示為 inline。
        fillRect(r3, ca, r3, cc, C.white);
        sheet.mergeCells(r3, ca, r3, cb);
        const noteCell = cl(r3, ca);
        noteCell.value = { richText: [
          { text: '備註: ', font: { name: 'Microsoft JhengHei', size: 6, color: { argb: C.mid } } },
          { text: note || '', font: { name: 'Microsoft JhengHei', size: 7, color: { argb: C.dark } } },
        ]};
        noteCell.fill      = solid(C.white);
        noteCell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true, indent: 1 };
        noteCell.border    = { top: iBord, left: oBord, bottom: oBord, right: iBord };

        const shelfCell = cl(r3, cc);
        shelfCell.value = { richText: [
          { text: '保存期限: ', font: { name: 'Microsoft JhengHei', size: 6, color: { argb: C.mid } } },
          { text: shelfLife,    font: { name: 'Microsoft JhengHei', size: 7, color: { argb: C.dark } } },
        ]};
        shelfCell.fill      = solid(C.white);
        shelfCell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true, indent: 1 };
        shelfCell.border    = { top: iBord, left: iBord, bottom: oBord, right: oBord };
      }

      curRow += 4;
    }

    // 頁尾分頁符（最後一頁不加）
    if (pageIdx < pages.length - 1) {
      sheet.getRow(curRow - 1).addPageBreak();
    }
  }

  // ── 下載
  // 檔名使用目錄標題（需求 R7）
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename || title || '產品型錄'}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * PDF 匯出參數說明
 * @param {Object} opts
 * @param {Array}  opts.products     - 產品列表
 * @param {Object} opts.companyInfo  - { name, nameEn, address, addressEn, tel }
 * @param {string} opts.title        - 型錄標題
 * @param {Object} opts.contact      - { name, phone, lineId, email }
 * @param {string} opts.filename     - 輸出檔名（不含副檔名）
 */
async function buildCatalogHtml({ products, companyInfo, title, contact }) {
  const imageDataMap = {};
  products.forEach((p) => {
    if (p.productImage) {
      imageDataMap[p._id] = p.productImage.startsWith('http') 
        ? p.productImage 
        : `${window.location.origin}${p.productImage.startsWith('/') ? '' : '/'}${p.productImage}`;
    }
  });

  const pageGroups = chunkArray(products, CARDS_PER_PAGE);
  const pagesHtml = pageGroups.map((pageProducts) =>
    buildPageHtml({ products: pageProducts, companyInfo, title, contact, imageDataMap })
  ).join('');

  return buildFullDocument(pagesHtml, title);
}

export async function exportCatalogPdf(opts) {
  const fullHtml = await buildCatalogHtml(opts);
  const originalTitle = document.title;

  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;top:0;left:0;width:0;height:0;border:none;opacity:0;';
  document.body.appendChild(iframe);

  const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
  iframeDoc.open();
  iframeDoc.write(fullHtml);
  iframeDoc.close();

  iframe.onload = () => {
    setTimeout(() => {
      document.title = opts.title || '產品型錄';
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      setTimeout(() => {
        document.title = originalTitle;
        document.body.removeChild(iframe);
      }, 1000);
    }, 300);
  };
}

export async function previewCatalog(opts) {
  const fullHtml = await buildCatalogHtml(opts);
  const win = window.open('', '_blank');
  win.document.open();
  win.document.write(fullHtml);
  win.document.close();
}

// 完整 HTML 文件

function buildFullDocument(pagesHtml, title) {
  return `<!DOCTYPE html>
<html lang="zh-TW">
<head>
  <meta charset="UTF-8">
  <title>${escHtml(title)}</title>
  <link href="https://fonts.googleapis.com/css2?family=Noto+Sans+TC:wght@300;400;500;700&display=swap" rel="stylesheet">
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }

    body {
      font-family: 'Noto Sans TC', -apple-system, BlinkMacSystemFont, 'Microsoft JhengHei', sans-serif;
      background: #e0e0e0;
      color: #5D4037;
      /* 需求 R7（PM 2026-05-18）：保留所有彩色背景，避免 Chrome / Safari 列印時
         自動移除非文字色塊，導致實際 PDF 與「預覽」分頁顏色出現落差。 */
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    /* ── A4 頁面 ── */
    .catalog-page {
      width: 210mm;
      height: 297mm;
      margin: 0 auto 20px;
      background: #fff;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      page-break-after: always;
    }

    /* ── 表頭 ── */
    /* 需求 R7（PM 2026-05-18）：原本使用 linear-gradient(#FFFBF0 → #FFECB3)
       在螢幕「預覽」呈現漸層米黃，但實際 PDF 列印時 Chrome 會把漸層降階成接近
       #FFF8E1 的單色（且各 PDF viewer 差異更大），導致 PM 看到「預覽 / 實際 PDF /
       Excel」三者顏色不一致。改用 Excel 端 C.headerBg 完全相同的 #FFF8E1 純色，
       讓三個輸出來源視覺一致。 */
    .catalog-header {
      padding: 14px 16px;
      background: #FFF8E1;
      border-bottom: 2px solid #FFD54F;
      flex-shrink: 0;
    }
    .header-top {
      display: flex;
      justify-content: space-between;
      align-items: stretch;
    }
    .company-info {
      display: flex;
      align-items: center;
      gap: 10px;
    }
    .company-logo {
      width: 60px; height: 60px;
      border-radius: 8px;
      display: flex; align-items: center; justify-content: center;
      font-weight: 700; font-size: 14px;
      color: #8D6E63;
      flex-shrink: 0;
      overflow: hidden;
    }
    .company-logo img {
      width: 100%; height: 100%; object-fit: contain;
    }
    .company-details h1 {
      font-size: 16px; color: #5D4037; margin-bottom: 2px;
    }
    .company-details p {
      font-size: 10px; color: #8D6E63; line-height: 1.5;
    }
    .catalog-title-block {
      display: flex; flex-direction: column;
      justify-content: space-between; align-items: flex-end;
    }
    .catalog-title-badge {
      font-size: 11px; color: #5D4037;
      background: #FFD54F;
      padding: 3px 8px;
      border-radius: 4px;
      white-space: nowrap;
      font-weight: 700;
    }
    .contact-info {
      margin-top: 4px;
      display: flex; flex-direction: column;
      align-items: flex-end; gap: 1px;
    }
    .contact-info .contact-title {
      font-size: 9px; color: #8D6E63;
    }
    .contact-info .contact-detail {
      font-size: 9px; color: #5D4037; line-height: 1.4;
    }

    /* ── 卡片網格 ── */
    .catalog-body {
      flex: 1;
      display: flex;
      align-items: flex-start;
      justify-content: center;
    }
    .products-grid {
      display: grid;
      grid-template-columns: repeat(3, 6.5cm);
      grid-template-rows: repeat(4, 6.5cm);
      gap: 0;
    }

    /* ── 產品卡片 ── */
    .product-card {
      width: 6.5cm;
      height: 6.5cm;
      border: 1px solid #d4b483;
      overflow: hidden;
    }
    .product-card-empty {
      background: #fafafa;
    }
    /* 需求 R93（比照 需求 R91 報價單）：產品圖區塊底色 #F0E0B2 → 白色，
       文字色 #8D6E63 維持不變。有圖片時 object-fit:contain 的留白處、
       無圖片時「產品圖」placeholder 皆改為白底。 */
    .product-image {
      width: 100%; height: 4.5cm;
      background: #fff;
      display: flex; align-items: center; justify-content: center;
      font-size: 11px; color: #8D6E63;
      border-bottom: 1px solid #e6c9a3;
      overflow: hidden;
    }
    .product-image img {
      width: 100%; height: 100%; object-fit: contain;
    }
    .product-info {
      height: 2.0cm;
      padding: 2px 3px;
      display: flex; flex-direction: column;
      justify-content: space-between;
      overflow: hidden;
    }

    /* ── 資訊行 ── */
    .info-row {
      display: flex;
      border-bottom: 1px solid #e6c9a3;
      font-size: 9px; line-height: 1.4;
    }
    .info-row:last-child { border-bottom: none; }

    .info-row.three-cols .info-cell {
      flex: 1; padding: 1px 3px;
      border-right: 1px solid #e6c9a3;
      min-width: 0; /* 允許 flex 子項收縮，讓「規格」文字正常換行而非撐開欄寬 */
    }
    .info-row.three-cols .info-cell:last-child { border-right: none; }
    /* 需求 R78（PM 2026-07-21 附圖回饋）：規格字元較多時原本三欄等寬會使「規格」跳成多行，
       把固定 2.0cm 高、overflow:hidden 的 .product-info 內容撐破，最底列「備註 / 保存期限」被裁切。
       貨編最多 7 個字元，縮小「貨編」欄寬（約 1.25cm 足容 7 個等寬字），把騰出空間全給「規格」欄，
       讓規格盡量維持單行、避免跳行造成下方資訊被裁掉。 */
    .info-row.three-cols .info-cell.cell-code { flex: 0 0 1.25cm; }
    .info-row.three-cols .info-cell.cell-spec { flex: 1.7 1 0; }

    .info-row.single-col .info-cell {
      flex: 1; padding: 1px 3px;
    }

    .info-row.two-cols .info-cell {
      flex: 2; padding: 1px 3px;
      border-right: 1px solid #e6c9a3;
      display: flex; flex-direction: row; align-items: center; gap: 2px;
      min-width: 0; /* 允許 flex 子項收縮，值才有機會被裁成單行而非撐開／換行 */
    }
    .info-row.two-cols .info-cell:last-child { flex: 1; border-right: none; }
    .info-row.two-cols .info-label { white-space: nowrap; margin-bottom: 0; flex: none; }
    .info-row.two-cols .info-label::after { content: ':'; }
    /* 需求 R95 R2（headless Chrome 以附件 att-1.png 的 A000023／A000031／A000032 實測復現）：
       本票把「備註」從幾乎永遠空白的 salesNotes 改讀 vegetarianType 後，這一列首次會有實際內容，
       長字串（例：保存期限「6月(夏季9個月)」、葷素備註「葷食，含五辛，蛋奶素者不可食用」）會換到第二行，
       而 .product-info 是固定 2.0cm + overflow:hidden，換行後整列被切掉一半 —— PM 附件圖第 2／3 張卡
       「保存期限」被裁成「6月(夏季」正是此現象，若不修，備註即使抓到資料仍可能看不到。
       比照 .product-name-zh／.product-name-en 既有做法，改為單行 + 省略號；
       短值（多數情況）的呈現與位置完全不變，符合 AC6「版面不變」。 */
    .info-row.two-cols .info-value {
      min-width: 0;
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    }

    .info-label {
      font-size: 7px; color: #8D6E63; display: block;
    }
    .info-value {
      color: #5D4037; font-weight: 500;
    }
    .product-code {
      font-family: 'Courier New', monospace;
      font-weight: 600;
    }
    .product-name-zh {
      font-weight: 600; color: #5D4037; font-size: 10px;
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    }
    .product-name-en {
      font-size: 7px; color: #8D6E63; font-style: italic;
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    }

    /* ── 列印樣式 ── */
    @media print {
      body { background: #fff; }
      .catalog-page {
        margin: 0;
        box-shadow: none;
        page-break-after: always;
      }
      @page {
        size: A4;
        margin: 0;
      }
    }
  </style>
</head>
<body>
  ${pagesHtml}
</body>
</html>`;
}

// 單頁 HTML

function buildPageHtml({ products, companyInfo, title, contact, imageDataMap }) {
  const {
    name = '',
    nameEn = '',
    address = '',
    addressEn = '',
    tel = '',
    fax = '',
  } = companyInfo;

  // 需求 R95（PM 拍板 Q1=A）：PDF／預覽卡片的「備註」同 Excel 改讀葷素備註 vegetarianType，
  // 不再傳業務備註 salesNotes（PDF 與 Excel 是兩條獨立路徑，兩處都要改才會一致）。
  const cards = products.map(p => buildProductCard(p, p.vegetarianType || '', imageDataMap)).join('');
  const emptyCount = CARDS_PER_PAGE - products.length;
  const emptyCards = Array(emptyCount).fill('<div class="product-card product-card-empty"></div>').join('');

  return `
<div class="catalog-page">
  <div class="catalog-header">
    <div class="header-top">
      <div class="company-info">
        <!-- 需求 R9：新增的公司沒有 Logo 時保持空白，不以公司名稱填補 -->
        <div class="company-logo">${companyInfo.logoUrl ? `<img src="${escHtml(companyInfo.logoUrl)}" alt="" />` : ''}</div>
        <div class="company-details">
          <h1>${escHtml(name)}</h1>
          <p>${escHtml(nameEn)}</p>
          ${address ? `<p>${escHtml(address)}</p>` : ''}
          ${addressEn ? `<p>${escHtml(addressEn)}</p>` : ''}
          ${(tel || fax) ? `<p>${tel ? `Tel: ${escHtml(tel)}` : ''}${tel && fax ? '　' : ''}${fax ? `Fax: ${escHtml(fax)}` : ''}</p>` : ''}
        </div>
      </div>
      <div class="catalog-title-block">
        <div class="catalog-title-badge">${escHtml(title)}</div>
        ${contact.name ? `
        <!-- 需求 R81 D2（PM 2026-07-22 拍板）：「聯絡人 Contact」區塊標題保留；僅移除欄位級標題（email 直接呈現值） -->
        <div class="contact-info">
          <span class="contact-title">聯絡人 Contact</span>
          <span class="contact-detail">${escHtml(contact.name)}</span>
          ${contact.email ? `<span class="contact-detail">${escHtml(contact.email)}</span>` : ''}
        </div>` : ''}
      </div>
    </div>
  </div>

  <div class="catalog-body">
    <div class="products-grid">
      ${cards}${emptyCards}
    </div>
  </div>
</div>`;
}

function buildProductCard(product, remark, imageDataMap) {
  const code = product.productCode || '—';
  const spec = product.spec || '—';
  const pcsPerBox = product.pcsPerBox != null ? `${product.pcsPerBox}入/箱` : '—';
  const nameZh = product.productName || '—';
  const nameEn = product.englishName || '';
  const shelfLife = product.shelfLife || '—';
  // 需求 R95（PM 拍板 Q1=A）：移除原本的 productNotes（產品備註）後備。
  // 舊寫法 `remark || product.productNotes || ''` 會讓「葷素備註為空」的產品在 PDF 顯示產品備註，
  // 而 Excel 端沒有這層後備 → 同一產品兩種輸出內容不同。Q1=A 要求「無值即空白」，故只用 remark。
  const note = remark || '';
  const dataUrl = imageDataMap[product._id] || '';

  const imageContent = dataUrl
    ? `<img src="${dataUrl}" alt="" />`
    : '產品圖';

  return `
<div class="product-card">
  <div class="product-image">${imageContent}</div>
  <div class="product-info">
    <div class="info-row three-cols">
      <div class="info-cell cell-code">
        <span class="info-label">貨編</span>
        <span class="info-value product-code">${escHtml(code)}</span>
      </div>
      <div class="info-cell cell-spec">
        <span class="info-label">規格</span>
        <span class="info-value">${escHtml(spec)}</span>
      </div>
      <div class="info-cell">
        <span class="info-label">箱入數</span>
        <span class="info-value">${escHtml(pcsPerBox)}</span>
      </div>
    </div>
    <div class="info-row single-col">
      <div class="info-cell">
        <div class="product-name-zh">${escHtml(nameZh)}</div>
        <div class="product-name-en">${nameEn ? escHtml(nameEn) : '&nbsp;'}</div>
      </div>
    </div>
    <div class="info-row two-cols">
      <div class="info-cell">
        <span class="info-label">備註</span>
        <span class="info-value">${escHtml(note)}</span>
      </div>
      <div class="info-cell">
        <span class="info-label">保存期限</span>
        <span class="info-value">${escHtml(shelfLife)}</span>
      </div>
    </div>
  </div>
</div>`;
}

function escHtml(str) {
  return String(str || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function chunkArray(arr, size) {
  const result = [];
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size));
  }
  return result;
}
